/**
 * WebSocket Hook
 * Manages WebSocket connections and real-time messaging
 */

import { useEffect, useRef } from 'react';
import { message as antMessage } from 'antd';
import { getUserCredentials } from '../auth/authUtils';
import { webSocketService } from '../websocket';
import { replaceOptimisticMessage, formatMessagePreview } from '../utils/commonUtils';
import { getConversations } from '../api/services/messagingService';
import { readCachedConversations, writeCachedConversations } from '../cache/messageCache';

export const useWebSocket = ({
  currentUser, setCurrentUser,
  selectedConversation, selectedConversationRef,
  messages, setMessages, setTyping,
  setReadReceipts, setConversations,
  setConversationsLoading,
  setOtherLastReadMessageId,
  scrollToBottom, setSelfLastReadMessageId,
  setOnlineUsernames, setUserPresence,
  setOtherLastReadMessageCreatedAt,
  setFirstUnreadIndex, setTypingByConversation,
  setVoiceRecordingByConversation
}) => {
  const userFeedUnsubRef = useRef(null);

  // Keep ref in sync
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation, selectedConversationRef]);

  // NOTE: auto-scrolling lives entirely in MessageList, which scrolls to the unread
  // divider / bottom on load and to the bottom on new messages only when the user is
  // already at the bottom. A previous effect here called scrollToBottom() with the
  // (inline, identity-changing) scrollToBottom in its deps, so it re-ran on EVERY
  // render and force-scrolled to the bottom — which made it impossible to scroll up
  // (so the jump-to-bottom button never stayed visible) and yanked the view away from
  // the "New messages" divider. It was removed.

  // Handle read receipt events
  const onReadEvent = (readEvent) => {
    setReadReceipts((prev) => ({
      ...prev,
      [readEvent.username]: readEvent.otherLastReadMessageId
    }));

    if (readEvent.username === currentUser?.username) {
      setSelfLastReadMessageId(readEvent.otherLastReadMessageId);
    } else {
      setOtherLastReadMessageId(readEvent.otherLastReadMessageId);
      setMessages((currentMessages) => {
        const otherLastReadIndex = readEvent.otherLastReadMessageId
          ? currentMessages.findIndex((m) => m.id === readEvent.otherLastReadMessageId)
          : -1;

        if (otherLastReadIndex >= 0) {
          setOtherLastReadMessageCreatedAt(currentMessages[otherLastReadIndex].createdAt);
        }

        return currentMessages;
      });
    }

    setConversations((prev) =>
      prev.map((c) =>
        c.id === readEvent.conversationId
          ? { ...c, isRead: true }
          : c
      )
    );
  };

  // Handle user feed events
  const handleUserFeedEvent = (event) => {
    if (!event?.type) return;

    switch (event.type) {
      case 'MESSAGE': {
        const msg = event.payload;
        if (!msg) return;
        const currentConv = selectedConversationRef.current;

        if (currentConv?.id === msg.conversationId) {
          replaceOptimisticMessage(setMessages, msg, setSelfLastReadMessageId, setFirstUnreadIndex);
          scrollToBottom();
          setOtherLastReadMessageId(msg.id);
        }
        updateSidebarWithMessage(setConversations, msg, currentConv);
        break;
      }

      case 'MESSAGE_EDIT': {
        const editedMsg = event.payload;
        if (!editedMsg) return;
        const currentConv = selectedConversationRef.current;

        // Update message in the message list and update any messages that replied to this message
        if (currentConv?.id === editedMsg.conversationId) {
          setMessages((prevMessages) =>
            prevMessages.map((msg) => {
              // Update the edited message itself
              if (msg.id === editedMsg.id) {
                return { ...msg, content: editedMsg.content, editedAt: editedMsg.editedAt };
              }
              // Update replyTo content if this message replied to the edited message
              if (msg.replyTo && msg.replyTo.id === editedMsg.id) {
                return {
                  ...msg,
                  replyTo: {
                    ...msg.replyTo,
                    content: editedMsg.content
                  }
                };
              }
              return msg;
            })
          );
        }

        // Update conversation sidebar if this was the last message
        updateSidebarOnMessageEdit(setConversations, editedMsg);
        break;
      }

      case 'MESSAGE_DELETE': {
        const deletedMsg = event.payload;
        if (!deletedMsg) return;
        const currentConv = selectedConversationRef.current;

        // Remove message from the list (not just mark as deleted)
        // and mark replyTo as deleted in messages that replied to this message
        if (currentConv?.id === deletedMsg.conversationId) {
          setMessages((prevMessages) =>
            prevMessages
              .filter((msg) => msg.id !== deletedMsg.id)
              .map((msg) => {
                // Mark replyTo as deleted if this message replied to the deleted message
                if (msg.replyTo && msg.replyTo.id === deletedMsg.id) {
                  return {
                    ...msg,
                    replyTo: {
                      ...msg.replyTo,
                      deleted: true,
                      content: null
                    }
                  };
                }
                return msg;
              })
          );
        }

        // Update conversation sidebar
        updateSidebarOnMessageDelete(setConversations, deletedMsg);
        break;
      }

      case 'CHAT_CLEARED': {
        const clearedChat = event.payload;
        if (!clearedChat) return;
        const currentConv = selectedConversationRef.current;

        // Clear all messages if we're in the cleared conversation
        if (currentConv?.id === clearedChat.conversationId) {
          setMessages([]);
        }

        // Update conversation sidebar
        setConversations((prev) =>
          prev.map((convo) =>
            convo.id === clearedChat.conversationId
              ? {
                  ...convo,
                  lastMessagePreview: null,
                  lastMessageId: null,
                  lastMessageDeleted: false,
                  unreadCount: 0
                }
              : convo
          )
        );
        break;
      }

      case 'CHAT_DELETED': {
        const deletedChat = event.payload;
        if (!deletedChat) return;
        const currentConv = selectedConversationRef.current;

        // Remove conversation from list
        setConversations((prev) =>
          prev.filter((convo) => convo.id !== deletedChat.conversationId)
        );

        // If we're in the deleted conversation, clear messages and deselect
        if (currentConv?.id === deletedChat.conversationId) {
          setMessages([]);
          // Note: The parent component should handle navigation back
        }
        break;
      }

      case 'CONVERSATION_CREATED': {
        const newConversation = event.payload;
        if (!newConversation) return;

        // Add the new conversation to the list if it doesn't already exist
        setConversations((prev) => {
          const exists = prev.some((c) => c.id === newConversation.id);
          if (exists) return prev;
          return [newConversation, ...prev];
        });
        break;
      }

      case 'READ_RECEIPT': {
        const receipt = event.payload;
        if (receipt) onReadEvent(receipt);
        break;
      }

      case 'TYPING': {
        const typingEvent = event.payload;
        const currentConv = selectedConversationRef.current;

        setTypingByConversation(prev => {
          const updated = new Map(prev);
          if (typingEvent.typing) {
            updated.set(typingEvent.conversationId, {
              username: typingEvent.senderUsername || 'Someone'
            });
          } else {
            updated.delete(typingEvent.conversationId);
          }
          return updated;
        });

        if (currentConv?.id === typingEvent.conversationId) {
          setTyping(
            typingEvent.typing
              ? `${typingEvent.senderUsername || 'Someone'} is typing...`
              : ''
          );
        }
        break;
      }

      case 'VOICE_RECORDING': {
        const recordingEvent = event.payload;
        if (!recordingEvent || !setVoiceRecordingByConversation) break;

        setVoiceRecordingByConversation(prev => {
          const updated = new Map(prev);
          if (recordingEvent.recording) {
            updated.set(recordingEvent.conversationId, {
              username: recordingEvent.senderUsername || 'Someone'
            });
          } else {
            updated.delete(recordingEvent.conversationId);
          }
          return updated;
        });
        break;
      }

      case 'PRESENCE': {
        const presence = event.payload;
        if (presence) {
          setUserPresence(prev => new Map(prev).set(presence.username, {
            isOnline: presence.isOnline,
            lastSeen: presence.lastSeen
          }));
        }
        break;
      }

      default:
        break;
    }
  };

  // Initialize chat on mount
  useEffect(() => {
    // Set once the server has answered (or the hook is gone): a cached list
    // that arrives after that must not paint over the fresh one. A failed
    // fetch does not settle it — offline, the cached list is the messenger.
    let settled = false;

    const initializeChat = async () => {
      const user = getUserCredentials();
      if (user) setCurrentUser(user);
      setConversationsLoading(true);

      // Stale-while-revalidate: the list as it was last seen goes up at once,
      // and the network response replaces it a moment later.
      const cachedList = readCachedConversations(user?.username);
      cachedList.then((cached) => {
        if (settled || !cached) return;
        setConversations(cached);
        setConversationsLoading(false);
      });

      try {
        const convRes = await getConversations();
        settled = true;
        const list = Array.isArray(convRes.data) ? convRes.data : [];
        setConversations(list);
        writeCachedConversations(user?.username, list);
      } catch (e) {
        console.error('Chat init failed:', e);
        if (!(await cachedList)) antMessage.error('Failed to initialize chat');
      } finally {
        setConversationsLoading(false);
      }
    };

    initializeChat();
    return () => {
      settled = true;
    };
  }, [setCurrentUser, setConversations, setConversationsLoading]);

  // Handle online users
  useEffect(() => {
    if (!currentUser) return;

    const handleOnlineUsers = (users) => {
      setOnlineUsernames([...users]);
    };

    webSocketService.addMessageListener('onlineUsers', handleOnlineUsers);

    const requestOnlineUsers = () => {
      if (webSocketService.isConnected()) {
        setTimeout(() => {
          webSocketService.client.publish({
            destination: '/app/online',
            body: ''
          });
        }, 300);
      }
    };

    requestOnlineUsers();

    const connectionListener = (connected) => {
      if (connected) requestOnlineUsers();
    };

    webSocketService.addConnectionListener(connectionListener);

    return () => {
      webSocketService.removeMessageListener('onlineUsers', handleOnlineUsers);
      webSocketService.removeConnectionListener(connectionListener);
    };
  }, [currentUser, setOnlineUsernames]);

  // Subscribe to user conversations
  useEffect(() => {
    if (!currentUser) return;

    const subscribe = () => {
      if (userFeedUnsubRef.current) userFeedUnsubRef.current();
      userFeedUnsubRef.current =
        webSocketService.subscribeToUserConversations(
          currentUser.username,
          handleUserFeedEvent
        );
    };

    if (webSocketService.isConnected()) subscribe();

    const connListener = () => subscribe();
    webSocketService.addConnectionListener(connListener);

    return () => {
      if (userFeedUnsubRef.current) userFeedUnsubRef.current();
      webSocketService.removeConnectionListener(connListener);
    };
  }, [currentUser]);

  // Mark messages as read
  useEffect(() => {
    if (!selectedConversation || !messages.length) return;
    const lastMsg = messages[messages.length - 1];

    if (
      document.visibilityState === 'visible' &&
      lastMsg.senderUsername !== currentUser?.username
    ) {
      webSocketService.markAsRead(selectedConversation.id, lastMsg.id);
      setSelfLastReadMessageId(lastMsg.id);

      setConversations((prev) =>
        prev.map((convo) =>
          convo.id === lastMsg.conversationId
            ? {
                ...convo,
                unreadCount:
                  selectedConversationRef?.current?.id === lastMsg.conversationId
                    ? 0
                    : convo.unreadCount
              }
            : convo
        )
      );
    }
  }, [messages, selectedConversation, currentUser?.username, setSelfLastReadMessageId, setConversations, selectedConversationRef]);
};

/**
 * Update sidebar conversation list with new message
 */
function updateSidebarWithMessage(setConversations, message, currentConv) {
  setConversations((prev) => {
    // Check if conversation exists
    const conversationExists = prev.some((c) => c.id === message.conversationId);

    const preview = formatMessagePreview(message);

    if (!conversationExists) {
      // If conversation doesn't exist, we can't add it here because we don't have
      // all the conversation details. The CONVERSATION_CREATED event should handle this.
      // However, if the message arrives before the conversation event, we should still
      // show something. We'll create a minimal conversation entry.
      const newConvo = {
        id: message.conversationId,
        name: message.senderUsername,
        username: message.senderUsername,
        lastMessagePreview: preview,
        lastMessageCreatedAt: message.createdAt,
        lastMessageSenderUsername: message.senderUsername,
        lastMessageId: message.id,
        lastMessageDeleted: false,
        unreadCount: 1,
        isRead: false,
        isGroup: false
      };
      return [newConvo, ...prev];
    }

    return prev
      .map((convo) =>
        convo.id === message.conversationId
          ? {
              ...convo,
              lastMessagePreview: preview,
              lastMessageCreatedAt: message.createdAt,
              lastMessageSenderUsername: message.senderUsername,
              lastMessageId: message.id,
              lastMessageDeleted: false,
              unreadCount:
                currentConv?.id === message.conversationId
                  ? convo.unreadCount
                  : convo.unreadCount + 1,
              isRead:
                currentConv?.id === message.conversationId &&
                message.createdAt <= convo.otherLastReadMessageCreatedAt
            }
          : convo
      )
      .sort(
        (a, b) =>
          new Date(b.lastMessageCreatedAt) - new Date(a.lastMessageCreatedAt)
      );
  });
}

/**
 * Update sidebar when a message is edited
 */
function updateSidebarOnMessageEdit(setConversations, editedMsg) {
  setConversations((prev) =>
    prev.map((convo) =>
      convo.id === editedMsg.conversationId && convo.lastMessageId === editedMsg.id
        ? {
            ...convo,
            lastMessagePreview: editedMsg.content
          }
        : convo
    )
  );
}

/**
 * Update sidebar when a message is deleted
 */
function updateSidebarOnMessageDelete(setConversations, deletedMsg) {
  setConversations((prev) =>
    prev.map((convo) =>
      convo.id === deletedMsg.conversationId && convo.lastMessageId === deletedMsg.id
        ? {
            ...convo,
            lastMessagePreview: deletedMsg.previousMessagePreview || null,
            lastMessageId: deletedMsg.previousMessageId || null,
            lastMessageCreatedAt: deletedMsg.previousMessageCreatedAt || null,
            lastMessageSenderUsername: deletedMsg.previousMessageSenderUsername || null,
            lastMessageDeleted: !deletedMsg.previousMessageId
          }
        : convo
    )
  );
}

export default useWebSocket;

